import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Toaster } from '../components/ui/toaster';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { 
  Building2, MapPin, Phone, Mail, FileText, Camera, Loader2, 
  Clock, Award, Trash2, Plus, X, CheckCircle, Image as ImageIcon
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const EditBusinessProfilePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const profilePhotoRef = useRef(null);
  const galleryRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [provider, setProvider] = useState(null);
  const [categories, setCategories] = useState([]);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    category: '',
    services: [],
    location: '',
    description: '',
    yearsInBusiness: '',
    image: '',
    gallery: [],
    responseTime: 'Within 24 hours',
    businessHours: '',
    serviceArea: '',
    certifications: [],
    licenseNumber: ''
  });

  const [newService, setNewService] = useState('');
  const [newCertification, setNewCertification] = useState('');

  useEffect(() => {
    if (user?.userType !== 'professional') {
      navigate('/');
      return;
    }
    fetchData();
  }, [user]);

  const fetchData = async () => {
    try {
      const [providersRes, categoriesRes] = await Promise.all([
        axios.get(`${API_URL}/api/providers`),
        axios.get(`${API_URL}/api/categories`)
      ]);
      
      setCategories(categoriesRes.data);
      
      // Find provider by email or name
      let provider = providersRes.data.find(p => p.email === user?.email);
      if (!provider) {
        provider = providersRes.data.find(p => p.name === user?.name);
      }
      
      if (provider) {
        setProvider(provider);
        setFormData({
          name: provider.name || '',
          email: provider.email || user?.email || '',
          phone: provider.phone || '',
          category: provider.category || '',
          services: provider.services || [],
          location: provider.location || '',
          description: provider.description || '',
          yearsInBusiness: provider.yearsInBusiness || '',
          image: provider.image || '',
          gallery: provider.gallery || [],
          responseTime: provider.responseTime || 'Within 24 hours',
          businessHours: provider.businessHours || '',
          serviceArea: provider.serviceArea || '',
          certifications: provider.certifications || [],
          licenseNumber: provider.licenseNumber || ''
        });
      } else {
        toast({
          title: 'No Business Profile',
          description: 'Please create your business profile first from "Become a Pro" page.',
          variant: 'destructive'
        });
        navigate('/become-a-pro');
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load profile data',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e, type = 'profile') => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast({
        title: 'File too large',
        description: 'Please select an image smaller than 2MB',
        variant: 'destructive'
      });
      return;
    }

    // Convert to base64
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result;
      if (type === 'profile') {
        setFormData({ ...formData, image: base64 });
      } else if (type === 'gallery') {
        setFormData({ ...formData, gallery: [...formData.gallery, base64] });
      }
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveGalleryImage = (index) => {
    const newGallery = formData.gallery.filter((_, i) => i !== index);
    setFormData({ ...formData, gallery: newGallery });
  };

  const handleAddService = () => {
    if (newService.trim() && !formData.services.includes(newService.trim())) {
      setFormData({ ...formData, services: [...formData.services, newService.trim()] });
      setNewService('');
    }
  };

  const handleRemoveService = (service) => {
    setFormData({ ...formData, services: formData.services.filter(s => s !== service) });
  };

  const handleAddCertification = () => {
    if (newCertification.trim() && !formData.certifications.includes(newCertification.trim())) {
      setFormData({ ...formData, certifications: [...formData.certifications, newCertification.trim()] });
      setNewCertification('');
    }
  };

  const handleRemoveCertification = (cert) => {
    setFormData({ ...formData, certifications: formData.certifications.filter(c => c !== cert) });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      // Use custom category if "Other" was selected
      const finalCategory = formData.category === 'Other' ? formData.customCategory : formData.category;
      
      const updateData = {
        ...formData,
        category: finalCategory,
        yearsInBusiness: parseInt(formData.yearsInBusiness) || 0
      };

      await axios.patch(`${API_URL}/api/providers/${provider.id}`, updateData);
      
      toast({
        title: 'Success!',
        description: 'Business profile updated successfully'
      });
      
      // Optionally navigate to view profile
      setTimeout(() => {
        navigate(`/provider/${provider.id}`);
      }, 1500);
      
    } catch (error) {
      console.error('Error updating profile:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to update profile',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="flex justify-center items-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-orange-600" />
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Edit Business Profile</h1>
          <p className="text-gray-600 mt-2">Update your public business information</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Profile Photo */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Camera className="h-5 w-5" />
                Profile Photo
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-6">
                <div className="relative">
                  <div className="w-32 h-32 rounded-full bg-gray-200 overflow-hidden">
                    {formData.image ? (
                      <img src={formData.image} alt="Profile" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Building2 className="h-12 w-12 text-gray-400" />
                      </div>
                    )}
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    className="absolute bottom-0 right-0 rounded-full"
                    onClick={() => profilePhotoRef.current?.click()}
                  >
                    <Camera className="h-4 w-4" />
                  </Button>
                  <input
                    ref={profilePhotoRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handleImageUpload(e, 'profile')}
                  />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Upload a professional photo of your business or logo</p>
                  <p className="text-xs text-gray-500 mt-1">Max size: 2MB. Formats: JPG, PNG</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Basic Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Business Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label>Category *</Label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) => setFormData({ ...formData, category: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat.name} value={cat.name}>
                          {cat.name}
                        </SelectItem>
                      ))}
                      <SelectItem value="Other">Other (Specify Below)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Custom Category Input - Shows when "Other" is selected */}
              {formData.category === 'Other' && (
                <div className="bg-orange-50 border-2 border-orange-200 rounded-lg p-4">
                  <Label htmlFor="customCategory">Specify Your Service *</Label>
                  <Input
                    id="customCategory"
                    value={formData.customCategory || ''}
                    onChange={(e) => setFormData({ ...formData, customCategory: e.target.value })}
                    placeholder="e.g., Pool Maintenance, Window Cleaning, etc."
                    required
                  />
                  <p className="text-xs text-gray-600 mt-1">Please specify your service category</p>
                </div>
              )}

              <div>
                <Label>Description *</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={4}
                  placeholder="Describe your business and services..."
                  required
                />
              </div>

              <div>
                <Label>Email *</Label>
                  <Input
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    type="email"
                    required
                  />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Location *</Label>
                  <Input
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    placeholder="City, Province"
                    required
                  />
                </div>
                <div>
                  <Label>Years in Business</Label>
                  <Input
                    type="number"
                    value={formData.yearsInBusiness}
                    onChange={(e) => setFormData({ ...formData, yearsInBusiness: e.target.value })}
                    min="0"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Services Offered */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5" />
                Services Offered
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Input
                  value={newService}
                  onChange={(e) => setNewService(e.target.value)}
                  placeholder="Add a service..."
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddService())}
                />
                <Button type="button" onClick={handleAddService}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.services.map((service, idx) => (
                  <div key={idx} className="bg-orange-100 text-orange-800 px-3 py-1 rounded-full flex items-center gap-2">
                    <span>{service}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveService(service)}
                      className="hover:text-orange-900"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Business Details */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Business Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Business Hours</Label>
                <Input
                  value={formData.businessHours}
                  onChange={(e) => setFormData({ ...formData, businessHours: e.target.value })}
                  placeholder="e.g., Mon-Fri 9AM-5PM, Sat 10AM-2PM"
                />
              </div>

              <div>
                <Label>Service Area</Label>
                <Input
                  value={formData.serviceArea}
                  onChange={(e) => setFormData({ ...formData, serviceArea: e.target.value })}
                  placeholder="e.g., Greater Toronto Area, 50km radius"
                />
              </div>

              <div>
                <Label>Response Time</Label>
                <Select
                  value={formData.responseTime}
                  onValueChange={(value) => setFormData({ ...formData, responseTime: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Within 1 hour">Within 1 hour</SelectItem>
                    <SelectItem value="Within 2 hours">Within 2 hours</SelectItem>
                    <SelectItem value="Within 4 hours">Within 4 hours</SelectItem>
                    <SelectItem value="Within 24 hours">Within 24 hours</SelectItem>
                    <SelectItem value="Within 48 hours">Within 48 hours</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>License Number</Label>
                <Input
                  value={formData.licenseNumber}
                  onChange={(e) => setFormData({ ...formData, licenseNumber: e.target.value })}
                  placeholder="Professional license or certification number"
                />
              </div>
            </CardContent>
          </Card>

          {/* Certifications */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Award className="h-5 w-5" />
                Certifications & Credentials
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Input
                  value={newCertification}
                  onChange={(e) => setNewCertification(e.target.value)}
                  placeholder="Add certification..."
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddCertification())}
                />
                <Button type="button" onClick={handleAddCertification}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.certifications.map((cert, idx) => (
                  <div key={idx} className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full flex items-center gap-2">
                    <span>{cert}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveCertification(cert)}
                      className="hover:text-blue-900"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Portfolio Gallery */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ImageIcon className="h-5 w-5" />
                Portfolio Gallery
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => galleryRef.current?.click()}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Photo
                </Button>
                <input
                  ref={galleryRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleImageUpload(e, 'gallery')}
                />
                <p className="text-xs text-gray-500 mt-2">Max 2MB per image</p>
              </div>

              <div className="grid grid-cols-3 gap-4">
                {formData.gallery.map((img, idx) => (
                  <div key={idx} className="relative group">
                    <img
                      src={img}
                      alt={`Gallery ${idx + 1}`}
                      className="w-full h-32 object-cover rounded-lg"
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveGalleryImage(idx)}
                      className="absolute top-2 right-2 bg-red-600 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Submit Buttons */}
          <div className="flex gap-3">
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={() => navigate(-1)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-orange-600 hover:bg-orange-700 text-white"
              disabled={saving}
            >
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </div>
        </form>
      </div>

      <Footer />
      <Toaster />
    </div>
  );
};

export default EditBusinessProfilePage;
